import { useState } from "react";
import axios from "axios";
import { asPaginatedResponse, usePaginatedApi } from "../../../swr/useApi";
import { orderDisputesBuyerService } from "../../api_client";
import type {
  AttachmentInputDto,
  CreateOrderDisputeDto,
  OrderDisputeDto,
  OrderDisputeQueryDto,
} from "../../../api";
import { showErrorToast } from "../../../utils/toasts";

// The list endpoint returns the full OrderDisputeDto shape (SerializePaginated strips messages)
export type OrderDisputeListDto = OrderDisputeDto;
export type { OrderDisputeDto, CreateOrderDisputeDto, AttachmentInputDto };

export type MyOrderDisputesQuery = {
  page?: number;
  pageSize?: number;
  status?: string;
};

export function useMyOrderDisputes(query?: MyOrderDisputesQuery) {
  return usePaginatedApi<OrderDisputeListDto, MyOrderDisputesQuery>({
    key: "my-order-disputes",
    serviceMethod: async (q) =>
      asPaginatedResponse<OrderDisputeListDto>(
        orderDisputesBuyerService.listMyOrderDisputes(q as unknown as OrderDisputeQueryDto),
      ),
    query,
  });
}

export async function createOrderDispute(dto: CreateOrderDisputeDto): Promise<OrderDisputeDto | null> {
  try {
    const res = await orderDisputesBuyerService.createOrderDispute(dto);
    return res.data;
  } catch {
    return null;
  }
}

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export function useOrderDisputeAttachmentUpload() {
  const [isUploading, setIsUploading] = useState(false);

  const upload = async (file: File): Promise<AttachmentInputDto | null> => {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      showErrorToast("File exceeds the 10MB limit.");
      return null;
    }

    setIsUploading(true);
    try {
      const res = await orderDisputesBuyerService.getOrderDisputeUploadUrl({ contentType: file.type });
      const { uploadUrl, objectKey, headers } = res.data as unknown as {
        uploadUrl: string;
        objectKey: string;
        headers: Record<string, string>;
      };

      const putHeaders: Record<string, string> = { ...headers };
      if (!putHeaders["Content-Type"]) putHeaders["Content-Type"] = file.type;

      await axios.put(uploadUrl, file, { headers: putHeaders });

      return { objectKey, fileName: file.name, contentType: file.type, fileSize: file.size };
    } catch (error) {
      console.error("Dispute attachment upload error:", error);
      showErrorToast("Failed to upload attachment. Please try again.");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return { isUploading, upload };
}
